import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  FolderKanban,
  FolderOpen,
  LogOut,
  MessagesSquare,
  Brain,
  Blocks,
  CalendarClock,
  CalendarCheck2,
  FileOutput,
  Settings,
  Search,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import type { StatusPayload } from "../types";

interface GlobalDrawerProps {
  open: boolean;
  status: StatusPayload | null;
  projectCount: number;
  sessionCount: number;
  taskCount: number;
  pendingApprovalCount: number;
  onClose(): void;
  onOpenToday(): void;
  onOpenSearch(): void;
  onOpenArtifacts(): void;
  onOpenProjects(): void;
  onOpenSessions(): void;
  onOpenRecurringTasks(): void;
  onOpenMemory(): void;
  onOpenFiles(): void;
  onOpenCapabilities(): void;
  onOpenConfiguration(): void;
  onLogout(): Promise<void>;
}

export function GlobalDrawer(props: GlobalDrawerProps) {
  return (
    <>
      <button
        className={`global-drawer-backdrop ${props.open ? "open" : ""}`}
        type="button"
        onClick={props.onClose}
        aria-label="Fermer le menu"
      />
      <aside className={`global-drawer ${props.open ? "open" : ""}`} aria-label="Menu principal">
        <header className="global-drawer-heading">
          <div className="brand-lockup">
            <span className="nora-logo" aria-hidden="true" />
            <div><strong>Nora</strong><span>Menu principal</span></div>
          </div>
          <button className="icon-command" type="button" onClick={props.onClose} aria-label="Fermer"><X aria-hidden="true" /></button>
        </header>

        <nav className="global-navigation" aria-label="Navigation principale">
          <DrawerCommand
            icon={<CalendarCheck2 aria-hidden="true" />}
            label="Aujourd’hui"
            meta={props.pendingApprovalCount
              ? `${props.pendingApprovalCount} validation${props.pendingApprovalCount > 1 ? "s" : ""} en attente`
              : "Décisions et points d’attention"}
            attention={props.pendingApprovalCount > 0}
            onClick={props.onOpenToday}
          />
          <DrawerCommand icon={<Search aria-hidden="true" />} label="Recherche" meta="Tout retrouver rapidement" onClick={props.onOpenSearch} />
          <DrawerCommand icon={<FolderKanban aria-hidden="true" />} label="Projets" meta={`${props.projectCount}`} onClick={props.onOpenProjects} />
          <DrawerCommand icon={<MessagesSquare aria-hidden="true" />} label="Conversations" meta={`${props.sessionCount}`} onClick={props.onOpenSessions} />
          <DrawerCommand icon={<CalendarClock aria-hidden="true" />} label="Tâches récurrentes" meta={`${props.taskCount} configurée${props.taskCount > 1 ? "s" : ""}`} onClick={props.onOpenRecurringTasks} />
          <DrawerCommand icon={<Brain aria-hidden="true" />} label="Mémoire" meta="Souvenirs et personnalité" onClick={props.onOpenMemory} />
          <DrawerCommand icon={<FileOutput aria-hidden="true" />} label="Livrables" meta="Résultats produits par Nora" onClick={props.onOpenArtifacts} />
          <DrawerCommand icon={<FolderOpen aria-hidden="true" />} label="Fichiers" meta="Workspaces et documents" onClick={props.onOpenFiles} fileOpenIntent />
          <DrawerCommand icon={<Blocks aria-hidden="true" />} label="Capacités" meta="Skills, MCP et API" onClick={props.onOpenCapabilities} />
          <DrawerCommand icon={<Settings aria-hidden="true" />} label="Configuration" meta="Modèles et droits" onClick={props.onOpenConfiguration} />
        </nav>

        <div className="global-runtime">
          <span className={`runtime-state ${props.status?.worker ? "healthy" : "warning"}`}>
            {props.status?.worker ? <CheckCircle2 aria-hidden="true" /> : <AlertTriangle aria-hidden="true" />}
          </span>
          <span>
            <strong>{props.status?.worker ? "Services disponibles" : "Worker indisponible"}</strong>
            <small>API · Worker · {props.status?.codex.available ? "Codex" : "Quotas indisponibles"}</small>
          </span>
        </div>

        <footer className="global-drawer-footer">
          <button className="drawer-logout" type="button" onClick={() => void props.onLogout()}>
            <LogOut aria-hidden="true" /><span>Déconnexion</span>
          </button>
        </footer>
      </aside>
    </>
  );
}

function DrawerCommand(props: {
  icon: ReactNode;
  label: string;
  meta: string;
  active?: boolean;
  attention?: boolean;
  disabled?: boolean;
  fileOpenIntent?: boolean;
  onClick(): void;
}) {
  return (
    <button
      className={[props.active ? "active" : "", props.attention ? "attention" : ""].filter(Boolean).join(" ") || undefined}
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-file-open-intent={props.fileOpenIntent ? "true" : undefined}
      aria-current={props.active ? "page" : undefined}
    >
      <span className="drawer-command-icon">{props.icon}</span>
      <span className="drawer-command-copy"><strong>{props.label}</strong><small>{props.meta}</small></span>
      <ChevronRight aria-hidden="true" />
    </button>
  );
}
